import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { 
  Cloud, Settings, Zap, Lock, AlertCircle, CheckCircle, 
  Globe, Brain, Sparkles, DollarSign
} from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

const ExternalRAGAgentForm = ({ onSubmit, onCancel, knowledgeBases = [], categories = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    provider: '',
    model_name: '',
    endpoint_url: '',
    api_key: '',
    region: 'us-east-1',
    project_id: '',
    resource_group: '',
    knowledge_base_ids: [],
    categories: [],
    system_prompt: 'You are an expert IT incident response specialist with access to comprehensive knowledge bases. Analyze incidents thoroughly and provide structured, actionable recommendations based on established procedures and best practices.',
    temperature: 0.7,
    max_tokens: 2000,
    confidence_threshold: 0.7,
    auto_assign: false,
    tags: ''
  });

  const [selectedProvider, setSelectedProvider] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [errors, setErrors] = useState({});

  const providers = [
    {
      id: 'aws_bedrock',
      name: 'AWS Bedrock',
      description: 'Amazon Bedrock foundation models',
      icon: Cloud,
      color: 'bg-orange-100 text-orange-600',
      models: [
        { id: 'anthropic.claude-3-sonnet-20240229-v1:0', name: 'Claude 3 Sonnet', cost: 'High Performance' },
        { id: 'anthropic.claude-3-haiku-20240307-v1:0', name: 'Claude 3 Haiku', cost: 'Cost Effective' },
        { id: 'meta.llama2-70b-chat-v1', name: 'Llama 2 70B', cost: 'Balanced' },
        { id: 'amazon.titan-text-express-v1', name: 'Amazon Titan Text', cost: 'Budget Friendly' }
      ],
      fields: ['api_key', 'region']
    },
    {
      id: 'gcp_vertex',
      name: 'Google Cloud Vertex AI',
      description: 'Google Cloud Vertex AI models',
      icon: Globe,
      color: 'bg-blue-100 text-blue-600',
      models: [
        { id: 'gemini-pro', name: 'Gemini Pro', cost: 'Moderate' },
        { id: 'text-bison-001', name: 'PaLM Text Bison', cost: 'Cost Effective' },
        { id: 'chat-bison-001', name: 'PaLM Chat Bison', cost: 'Conversational' }
      ],
      fields: ['api_key', 'project_id', 'region']
    },
    {
      id: 'azure_openai',
      name: 'Azure OpenAI',
      description: 'Microsoft Azure OpenAI Service',
      icon: Cloud,
      color: 'bg-blue-100 text-blue-800',
      models: [
        { id: 'gpt-4', name: 'GPT-4', cost: 'Premium' },
        { id: 'gpt-35-turbo', name: 'GPT-3.5 Turbo', cost: 'Standard' },
        { id: 'gpt-35-turbo-16k', name: 'GPT-3.5 Turbo 16K', cost: 'Extended Context' }
      ],
      fields: ['api_key', 'endpoint_url', 'resource_group']
    },
    {
      id: 'anthropic',
      name: 'Anthropic',
      description: 'Anthropic Claude API',
      icon: Brain,
      color: 'bg-purple-100 text-purple-600',
      models: [
        { id: 'claude-3-opus', name: 'Claude 3 Opus', cost: 'Premium' },
        { id: 'claude-3-sonnet', name: 'Claude 3 Sonnet', cost: 'Balanced' },
        { id: 'claude-3-haiku', name: 'Claude 3 Haiku', cost: 'Fast' }
      ],
      fields: ['api_key']
    },
    {
      id: 'cohere',
      name: 'Cohere',
      description: 'Cohere language models',
      icon: Sparkles,
      color: 'bg-green-100 text-green-600',
      models: [
        { id: 'command', name: 'Command', cost: 'Standard' },
        { id: 'command-light', name: 'Command Light', cost: 'Lightweight' }
      ],
      fields: ['api_key']
    },
    {
      id: 'huggingface',
      name: 'Hugging Face',
      description: 'Hugging Face Inference API',
      icon: Globe,
      color: 'bg-yellow-100 text-yellow-600',
      models: [
        { id: 'mistralai/Mixtral-8x7B-Instruct-v0.1', name: 'Mixtral 8x7B', cost: 'Open Source' },
        { id: 'microsoft/DialoGPT-large', name: 'DialoGPT Large', cost: 'Conversational' }
      ],
      fields: ['api_key']
    }
  ];

  const getCurrentProvider = () => providers.find(p => p.id === selectedProvider);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!selectedProvider) newErrors.provider = 'Provider is required';
    if (!formData.model_name) newErrors.model_name = 'Model is required';
    
    const provider = getCurrentProvider();
    if (provider) {
      if (provider.fields.includes('api_key') && !formData.api_key.trim()) {
        newErrors.api_key = 'API key is required';
      }
      if (provider.fields.includes('endpoint_url') && !formData.endpoint_url.trim()) {
        newErrors.endpoint_url = 'Endpoint URL is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const submitData = {
      ...formData,
      provider: selectedProvider,
      tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
    };

    try {
      await onSubmit(submitData);
    } catch (error) {
      setErrors({ submit: error.message });
    }
  };

  const handleProviderChange = (providerId) => {
    setSelectedProvider(providerId);
    setFormData(prev => ({
      ...prev,
      model_name: '',
      endpoint_url: '',
      api_key: '',
      region: providerId === 'aws_bedrock' ? 'us-east-1' : prev.region,
      project_id: '',
      resource_group: ''
    }));
  };

  const getCostBadgeColor = (cost) => {
    switch (cost) {
      case 'Budget Friendly': 
      case 'Cost Effective': return 'bg-green-100 text-green-800';
      case 'Premium': 
      case 'High Performance': return 'bg-red-100 text-red-800';
      case 'Open Source': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5" />
            Create External RAG Agent
          </CardTitle>
          <p className="text-sm text-gray-600">
            Integrate with external cloud providers to leverage their advanced language models
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Agent Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., AWS Bedrock Incident Analyzer"
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the agent's purpose"
                />
              </div>
            </div>

            {/* Provider Selection */}
            <div className="space-y-4">
              <label className="text-sm font-medium">Cloud Provider *</label>
              {errors.provider && <p className="text-xs text-red-500">{errors.provider}</p>}
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {providers.map((provider) => {
                  const IconComponent = provider.icon;
                  const isSelected = selectedProvider === provider.id;
                  
                  return (
                    <Card
                      key={provider.id}
                      className={`cursor-pointer transition-all ${
                        isSelected 
                          ? 'ring-2 ring-blue-500 bg-blue-50' 
                          : 'hover:shadow-md'
                      }`}
                      onClick={() => handleProviderChange(provider.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${provider.color}`}>
                            <IconComponent size={20} />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-sm">{provider.name}</h3>
                            <p className="text-xs text-gray-600 mt-1">{provider.description}</p>
                            <div className="mt-2">
                              <Badge variant="outline" className="text-xs">
                                {provider.models.length} models
                              </Badge>
                            </div>
                          </div>
                          {isSelected && (
                            <CheckCircle className="h-5 w-5 text-blue-500 flex-shrink-0" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Model Selection */}
            {selectedProvider && (
              <div className="space-y-4">
                <label className="text-sm font-medium">Model *</label>
                {errors.model_name && <p className="text-xs text-red-500">{errors.model_name}</p>}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {getCurrentProvider()?.models.map((model) => (
                    <Card
                      key={model.id}
                      className={`cursor-pointer transition-all ${
                        formData.model_name === model.id
                          ? 'ring-2 ring-blue-500 bg-blue-50'
                          : 'hover:shadow-md'
                      }`}
                      onClick={() => setFormData(prev => ({ ...prev, model_name: model.id }))}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-sm">{model.name}</h4>
                            <p className="text-xs text-gray-600">{model.id}</p>
                          </div>
                          <Badge className={getCostBadgeColor(model.cost)}>
                            {model.cost}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Provider Configuration */}
            {selectedProvider && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Configuration
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {getCurrentProvider()?.fields.includes('api_key') && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium flex items-center gap-2">
                        <Lock className="h-4 w-4" />
                        API Key *
                      </label>
                      <Input
                        type="password"
                        value={formData.api_key}
                        onChange={(e) => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                        placeholder="Enter your API key"
                        className={errors.api_key ? 'border-red-500' : ''}
                      />
                      {errors.api_key && <p className="text-xs text-red-500">{errors.api_key}</p>}
                    </div>
                  )}
                  
                  {getCurrentProvider()?.fields.includes('endpoint_url') && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Endpoint URL *</label>
                      <Input
                        value={formData.endpoint_url}
                        onChange={(e) => setFormData(prev => ({ ...prev, endpoint_url: e.target.value }))}
                        placeholder="https://your-endpoint.openai.azure.com/"
                        className={errors.endpoint_url ? 'border-red-500' : ''}
                      />
                      {errors.endpoint_url && <p className="text-xs text-red-500">{errors.endpoint_url}</p>}
                    </div>
                  )}
                  
                  {getCurrentProvider()?.fields.includes('region') && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Region</label>
                      <Select value={formData.region} onValueChange={(value) => setFormData(prev => ({ ...prev, region: value }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="us-east-1">US East (N. Virginia)</SelectItem>
                          <SelectItem value="us-west-2">US West (Oregon)</SelectItem>
                          <SelectItem value="eu-west-1">Europe (Ireland)</SelectItem>
                          <SelectItem value="ap-southeast-1">Asia Pacific (Singapore)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {getCurrentProvider()?.fields.includes('project_id') && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Project ID</label>
                      <Input
                        value={formData.project_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, project_id: e.target.value }))}
                        placeholder="your-gcp-project-id"
                      />
                    </div>
                  )}
                  
                  {getCurrentProvider()?.fields.includes('resource_group') && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Resource Group</label>
                      <Input
                        value={formData.resource_group}
                        onChange={(e) => setFormData(prev => ({ ...prev, resource_group: e.target.value }))}
                        placeholder="your-resource-group"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Knowledge Bases and Categories */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Knowledge Bases</label>
                <div className="max-h-32 overflow-y-auto space-y-2 border rounded-md p-2">
                  {knowledgeBases.map((kb) => (
                    <div key={kb.id} className="flex items-center space-x-2">
                      <Checkbox
                        checked={formData.knowledge_base_ids.includes(kb.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData(prev => ({
                              ...prev,
                              knowledge_base_ids: [...prev.knowledge_base_ids, kb.id]
                            }));
                          } else {
                            setFormData(prev => ({
                              ...prev,
                              knowledge_base_ids: prev.knowledge_base_ids.filter(id => id !== kb.id)
                            }));
                          }
                        }}
                      />
                      <span className="text-sm">{kb.name}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Categories</label>
                <div className="max-h-32 overflow-y-auto space-y-2 border rounded-md p-2">
                  {categories.map((category) => (
                    <div key={category} className="flex items-center space-x-2">
                      <Checkbox
                        checked={formData.categories.includes(category)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData(prev => ({
                              ...prev,
                              categories: [...prev.categories, category]
                            }));
                          } else {
                            setFormData(prev => ({
                              ...prev,
                              categories: prev.categories.filter(c => c !== category)
                            }));
                          }
                        }}
                      />
                      <span className="text-sm">{category}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* System Prompt */}
            <div className="space-y-2">
              <label className="text-sm font-medium">System Prompt</label>
              <Textarea
                value={formData.system_prompt}
                onChange={(e) => setFormData(prev => ({ ...prev, system_prompt: e.target.value }))}
                rows={4}
                placeholder="Define the agent's role and behavior..."
              />
            </div>

            {/* Advanced Settings */}
            <div className="space-y-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2"
              >
                <Settings className="h-4 w-4" />
                {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
              </Button>

              {showAdvanced && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border rounded-lg bg-gray-50">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Temperature</label>
                    <Input
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      value={formData.temperature}
                      onChange={(e) => setFormData(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Max Tokens</label>
                    <Input
                      type="number"
                      min="1"
                      max="4000"
                      value={formData.max_tokens}
                      onChange={(e) => setFormData(prev => ({ ...prev, max_tokens: parseInt(e.target.value) }))}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Confidence Threshold</label>
                    <Input
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      value={formData.confidence_threshold}
                      onChange={(e) => setFormData(prev => ({ ...prev, confidence_threshold: parseFloat(e.target.value) }))}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Tags</label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                placeholder="production, incident-response, automated (comma-separated)"
              />
            </div>

            {/* Error Display */}
            {errors.submit && (
              <Alert className="border-red-200">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  {errors.submit}
                </AlertDescription>
              </Alert>
            )}

            {/* Actions */}
            <div className="flex gap-4 pt-4">
              <Button type="submit" className="flex-1">
                Create External RAG Agent
              </Button>
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExternalRAGAgentForm;